import { get } from 'lodash';
import { isExperienceApiModel } from '../../../common/helpers';
import type { DCPBadgeDTO, DCPColorVariantsDTO, IEmicoData, SMSEnumDTO, SMSProduct } from '../../shared/ocb';
import type {
  // ColorVariant,
  EmicoItem,
  FormattedData,
  // Identification,
  LabelDescription,
  ProductDetail,
} from '../product-details.model';

type ProductDetailKeys =
  | 'badges'
  | 'baumuster'
  | 'bodyType'
  | 'brand'
  | 'code'
  | 'color'
  | 'commissionNumber'
  | 'name'
  | 'productType'
  | 'fuelType'
  | 'transmission'
  | 'colorVariants'
  | 'engineSizeInCcm'
  | 'numberOfCylinders'
  | 'modelYear'
  | 'length'
  | 'width'
  | 'height'
  | 'weight'
  | 'maxLoad'
  | 'maxWeight'
  | 'loadingSpaceLength'
  | 'loadingSpaceWidth'
  | 'loadingSpaceHeight'
  | 'loadingSpaceVolume'
  | 'maxSpeed'
  | 'driveType'
  | 'mileage'
  | 'numberOfDoors'
  | 'numberOfSeats'
  | 'upholstery'
  | 'emicoData'
  | 'emicoDataWltp'
  | 'vehicleId';

/**
 * the aim of this function is to return the Vehicle-based type of a specific parameter when the input product is either a ProductDetail or a Vehicle
 * @param product can be either ProductDetail or Vehicle
 * @param param is the prop we want to obtain
 * @returns the requested prop in the Vehicle-based type
 */
export const getProductDetailParam = <TParam extends ProductDetailKeys>(
  product: Partial<SMSProduct> | Partial<ProductDetail>,
  param: TParam,
) => {
  const isVehicleModel = !isExperienceApiModel(product);

  if (isVehicleModel) {
    return get(product, param);
  }

  const productDetailMapper = {
    badges: labelDescription2DCPBadgeDTO(product.characteristics?.badges),
    baumuster: product.vehicleModel?.baumuster,
    bodyType: formattedData2SMSEnumDTO(product.vehicleModel?.bodyType),
    brand: product.vehicleModel?.brand?.value,
    code: product.identification?.code,
    color: product.color?.text,
    // TODO: This colorVariants logic needs to be uncommented in the next RC since we didn't migrate yet colorVariants in this version. <-- See ticket: DMHB-2155
    // colorVariants: colorVariant2DCPColorVariantsDTO(product.identification, product.colorVariants),
    vehicleId: product.identification?.productId,
    commissionNumber: product.identification?.commissionNumber,
    name: product.vehicleModel?.name,
    productType: product.identification?.productType,
    fuelType: formattedData2SMSEnumDTO(product.technicalInformation?.engine?.fuelType),
    transmission: product.technicalInformation?.transmission?.transmissionCategory?.value,
    numberOfCylinders: product.technicalInformation?.engine?.cylinder?.value,
    modelYear: product.vehicleModel?.modelYear,
    width: product.technicalInformation?.dimension?.formattedValue,
    weight: product.technicalInformation?.weights?.unloadedWeight?.formattedValue,
    maxLoad: product.technicalInformation?.payload?.formattedValue,
    maxWeight: product.technicalInformation?.weights?.maximumWeight?.formattedValue,
    maxSpeed: product.technicalInformation?.topSpeed?.formattedValue,
    driveType: formattedData2SMSEnumDTO(product.technicalInformation?.engine?.typeOfPropulsion),
    numberOfDoors: product.technicalInformation?.doors?.value,
    numberOfSeats: product.technicalInformation?.seats?.value,
    emicoData: mapEmicoData(product.emico?.nefz),
    emicoDataWltp: mapEmicoData(product.emico?.wtlp),
    upholstery: product.upholstery?.text,
  } as const;

  return get(productDetailMapper, param);
};

const isEmpty = (data: any): boolean => Object.values(data).every((x) => x === undefined || x === null);

// const colorVariant2DCPColorVariantsDTO = (
//   productIdentification?: Identification,
//   colorVariants?: ColorVariant[],
// ): DCPColorVariantsDTO[] | undefined => {
//   if (!colorVariants || !productIdentification || isEmpty(colorVariants) || isEmpty(productIdentification)) return;

//   return colorVariants.map((colorVariant) => ({
//     colorCode: colorVariant.identification.code,
//     colorImage: colorVariant.color.image?.url,
//     colorName: colorVariant.color.text,
//     currentProduct: colorVariant.identification.productId === productIdentification.code,
//     productCode: colorVariant.identification.productId,
//   })) as DCPColorVariantsDTO[];
// };

const formattedData2SMSEnumDTO = (formattedData?: FormattedData<string>): SMSEnumDTO | undefined => {
  if (!formattedData || isEmpty(formattedData)) return;

  return {
    code: formattedData.value,
    name: formattedData.formattedValue,
  } as SMSEnumDTO;
};

const labelDescription2DCPBadgeDTO = (badges?: LabelDescription[]): DCPBadgeDTO[] | undefined => {
  if (!badges || isEmpty(badges)) return;

  return badges.map((badge) => ({ name: badge.label, description: badge.description })) as DCPBadgeDTO[];
};

const mapEmicoData = (data?: EmicoItem) =>
  data?.attributes?.map(
    (attributes) =>
      ({
        ...attributes,
        displayImage: attributes.displayImage?.url,
        footnoteText: data?.footnotes,
        order: 0,
      } as IEmicoData),
  );
